import { ClipboardIcon, EnvelopeIcon, UserPlusIcon } from "@heroicons/react/20/solid";
import {
  Flex,
  HoverCard,
  Paper,
  Text, UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMemo } from "react";
import { useIntegrationApp } from "@membranehq/react";
import { IconSparkles } from "@tabler/icons-react";
import SlackIcon from "../../../../../assets/images/slack.svg?react";
import { queryClient, useAllIntegrations } from "../../../../../hooks/QueryHooks";
import { getProvider } from "../../../../../integrations/providers";
import { runConnectionFlow } from "../../../../../integrations/integrationAppPostConnect";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import ActionCard from "./ActionCard";
import SlackModal from "./SlackModal";
import InboxModal from "./InboxModal";
import EmailModal from "./EmailModal";
import AssignPersonModal from "./AssignPersonModal";
import RunSkillsModal from "./RunSkillsModal";
import notify from "../../../../../utils/notify";

type Props = {
  updateAction: (action: any, type: any) => void;
  actions: any[];
  playbook: any;
  deleteAction: any;
  setSelectedFields: any;
  setSelectedMetrics: any;
  signal?: any;
  isAutomation?: boolean;
};

const Destinations = ({
  updateAction,
  deleteAction,
  actions,
  playbook,
  setSelectedFields,
  setSelectedMetrics,
  signal,
  isAutomation = false,
}: Props) => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const integrationApp = useIntegrationApp();
  const provider = getProvider("slack");
  const [slackOpened, { open: slackOpen, close: slackClose }] = useDisclosure(false);
  const [emailOpened, { open: emailOpen, close: emailClose }] = useDisclosure(false);
  const [inboxOpened, { open: inboxOpen, close: inboxClose }] = useDisclosure(false);
  const [assignPersonOpened, { open: assignPersonOpen, close: assignPersonClose }] = useDisclosure(false);
  const [runSkillsOpened, { open: runSkillsOpen, close: runSkillsClose }] = useDisclosure(false);

  const { data: allIntegrations }: any = useAllIntegrations({
    workspaceId: workspace.id,
    enabled: true,
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const slackTopLevelIntegration = useMemo(
    () => (allIntegrations || []).find((item: any) => item?.source_name === "slack" && !item?.is_member_level),
    [allIntegrations],
  );
  const slackIsConnected = Boolean(
    slackTopLevelIntegration?.setup_complete
    && (slackTopLevelIntegration?.credential_status_ok ?? true),
  );

  const slackEnabled = useMemo(() => actions.find((action: any) => action?.type === "send_to_slack"), [actions]);
  const emailEnabled = useMemo(() => actions.find((action: any) => action?.type === "send_to_email"), [actions]);
  const inboxEnabled = useMemo(() => actions.find((action: any) => action?.type === "send_to_inbox"), [actions]);
  const assignPersonEnabled = useMemo(() => actions.find((action: any) => action?.type === "assign_person"), [actions]);
  const runSkillsEnabled = useMemo(() => actions.find((action: any) => action?.type === "run_skills"), [actions]);

  const onCompleteAction = (action: any, type: string) => {
    updateAction(action, type);
  };

  const slackClick = async () => {
    try {
      const connection = await integrationApp.integration(provider.key).openNewConnection();
      if (connection && (connection as any)?.state === "READY") {
        if ((connection as any)?.id) {
          const connectionId = (connection as any).id as string;
          await Promise.allSettled([
            runConnectionFlow(integrationApp, connectionId, "sync-connection-details"),
            runConnectionFlow(integrationApp, connectionId, "first-sync-for-users"),
          ]);
        }
        notify({
          heading: "Integrated Slack",
          type: "success",
          messageList: ["You have successfully integrated Slack with Hyperengage"],
        });
        queryClient.invalidateQueries(["slack-integration", workspace?.id]);
        queryClient.invalidateQueries(["integrations", workspace?.id]);
        return;
      }
      throw new Error("Connection not ready");
    } catch (e) {
      notify({
        heading: "Integration Failed",
        type: "failure",
        messageList: ["Failed to integrate Slack with Hyperengage, please try again later"],
      });
    }
  };

  return (
    <Paper py="md">
      <Text component="label" className="text-gray-500 text-sm font-bold mb-2" htmlFor="action-type">Send via</Text>
      <SlackModal
        setSelectedMetrics={setSelectedMetrics}
        onCompleteAction={onCompleteAction}
        setSelectedFields={setSelectedFields}
        signal={signal}
        opened={slackOpened}
        close={slackClose}
        data={slackEnabled}
      />
      <EmailModal
        signal={signal}
        setSelectedFields={setSelectedFields}
        setSelectedMetrics={setSelectedMetrics}
        onCompleteAction={onCompleteAction}
        opened={emailOpened}
        close={emailClose}
        data={emailEnabled}
        playbook={playbook}
      />
      <InboxModal
        onCompleteAction={onCompleteAction}
        opened={inboxOpened}
        close={inboxClose}
        data={inboxEnabled}
      />
      <AssignPersonModal
        onCompleteAction={onCompleteAction}
        opened={assignPersonOpened}
        close={assignPersonClose}
        data={assignPersonEnabled}
        signal={signal}
      />
      <RunSkillsModal
        onCompleteAction={onCompleteAction}
        opened={runSkillsOpened}
        close={runSkillsClose}
        data={runSkillsEnabled}
      />
      <Flex wrap="wrap" justify="center" gap="lg">
        {!isAutomation && (
          <>
            <ActionCard
              action="Inbox"
              desc="Send alert to Hyperengage's inbuilt Inbox."
              Image={ClipboardIcon}
              onClick={() => {
                inboxOpen();
              }}
              onDelete={() => {
                deleteAction("send_to_inbox");
              }}
              complete={inboxEnabled}
            />
            <HoverCard
              openDelay={200}
              width={350}
              withArrow
              transitionProps={{ duration: 200 }}
            >
              <HoverCard.Target>
                <div>
                  <ActionCard
                    action="Slack"
                    info="Send alerts via Slack. You can only choose one of slack or email alerts."
                    desc="Send alert to a Slack channel."
                    Image={SlackIcon}
                    onClick={() => {
                      slackOpen();
                    }}
                    onDelete={() => {
                      deleteAction("send_to_slack");
                    }}
                    enabled={slackIsConnected}
                    complete={slackEnabled}
                  />
                </div>
              </HoverCard.Target>
              {!slackIsConnected && (
                <HoverCard.Dropdown className="bg-gray-100 text-sm">
                  <Text>
                    Looks like you currently don&apos;t have Slack integration enabled.
                    {" "}
                    <UnstyledButton className="text-sm" c="main.5" onClick={() => { slackClick(); }}>
                      Click here to Integrate Slack
                    </UnstyledButton>
                  </Text>
                </HoverCard.Dropdown>
              )}
            </HoverCard>
            <ActionCard
              action="Email"
              info="Send email alerts by adding recipients. You can only choose one of slack or email alerts"
              desc="Send alert to email addresses."
              Image={EnvelopeIcon}
              onClick={() => {
                emailOpen();
              }}
              onDelete={() => {
                deleteAction("send_to_email");
              }}
              complete={emailEnabled}
            />
          </>
        )}
        {isAutomation && (
          <ActionCard
            action="Assign Person"
            desc="Auto-assign CSMs/AEs to accounts based on strategies."
            Image={UserPlusIcon}
            onClick={() => {
              assignPersonOpen();
            }}
            onDelete={() => {
              deleteAction("assign_person");
            }}
            complete={assignPersonEnabled}
          />
        )}
      </Flex>

      {!isAutomation && (
        <>
          <Text component="label" className="text-gray-500 text-sm font-bold mb-2 mt-6 block" htmlFor="report">Report</Text>
          <Flex wrap="wrap" justify="center" gap="lg">
            <ActionCard
              action="AI Analysis"
              desc="Include an AI-generated analysis with each alert."
              Image={IconSparkles}
              onClick={runSkillsOpen}
              onDelete={() => {
                deleteAction("run_skills");
              }}
              complete={runSkillsEnabled}
            />
          </Flex>
        </>
      )}
    </Paper>
  );
};

export default Destinations;
